import { QuoteTabDto } from 'api/quote';
import clsx from 'clsx';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContractualizationTabs, formatPrice } from '../layout/ContractualizationTabs';

interface ContractSimulationsProps {
  activeTab?: string;
  tabs: QuoteTabDto[];
  /**
   * Under the file tabs, the quotes of the file are plain chips: two rows of browser-like tabs
   * would not tell which level is which.
   */
  asChips?: boolean;
}

/**
 * Tabs component for quote simulations (courtier flow)
 * Uses the generic ContractualizationTabs with quote-specific navigation
 */
export const ContractSimulations: React.FC<ContractSimulationsProps> = ({
  tabs,
  activeTab,
  asChips = false
}) => {
  const navigate = useNavigate();
  const { riskId } = useParams<{ riskId: string }>();

  const handleTabClick = useCallback(
    (tabId: string, _tab: QuoteTabDto) => {
      navigate(`/simulation/contractualisation/${riskId}/${tabId}`);
    },
    [navigate, riskId]
  );

  const getTabId = useCallback((tab: QuoteTabDto) => {
    return tab.id;
  }, []);

  const getTabName = useCallback((tab: QuoteTabDto) => {
    const splits = tab.label.split('-');
    return splits[3] || tab.label;
  }, []);

  if (asChips) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-8 py-4 border-b border-gray-100">
        {tabs.map(tab => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              aria-current={isActive || undefined}
              onClick={() => handleTabClick(tab.id, tab)}
              className={clsx(
                'flex items-center gap-2.5 h-11 pl-3.5 pr-2 rounded-lg border text-left transition-colors',
                isActive
                  ? 'border-primary/30 bg-primary/10'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              )}
            >
              <span className="flex flex-col min-w-0">
                <span className={clsx('text-[13px] leading-tight whitespace-nowrap', isActive ? 'text-primary font-bold' : 'text-gray-600 font-medium')}>
                  {`Devis N°${getTabName(tab)}`}
                </span>
                {tab.product && (
                  <span className={clsx('text-xs leading-tight whitespace-nowrap', isActive ? 'text-primary/70' : 'text-gray-400')}>
                    {tab.product}
                  </span>
                )}
              </span>
              {tab.clientPrice !== undefined && tab.clientPrice !== null && (
                <span
                  className={clsx(
                    'px-2 py-1 rounded-md text-xs font-bold tabular-nums whitespace-nowrap',
                    isActive ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {formatPrice(tab.clientPrice)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <ContractualizationTabs
      tabs={tabs}
      activeTab={activeTab}
      onTabClick={handleTabClick}
      getTabId={getTabId}
      getTabName={getTabName}
    />
  );
};
